/**
 * Cover layouts. Each slot says which panel its fractions belong to — `front`, `back`, or none for
 * the whole strip — so the same template lands right whatever the spine width or the binding.
 * Panel fractions use the page box (`innerBox('page')`), the same numbers a page layout would.
 *
 * Ids are `cv-<photos>-<name>`; the tag says which part of the cover a template is for:
 * `front`, `back`, or `wrap` (touches more than one panel).
 */
import type { Layout } from '@freealbum/document';
import { ALL_FAMILIES, FULL_BOX, GUTTER_FRAC, grid, innerBox, layout, slot, sub } from './_helpers';

const P = innerBox('page');
const G = GUTTER_FRAC;

export const COVER_LAYOUTS: Layout[] = [
  // ---------- front ----------
  layout({
    id: 'cv-1-front-full',
    name: 'Full-bleed front, title below',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'full-bleed', 'title', 'hero'],
    marginFrac: 0,
    slots: [
      slot({ id: 'a', panel: 'front', ...FULL_BOX }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.06, 0.78, 0.88, 0.14) }),
    ],
  }),
  layout({
    id: 'cv-1-front-framed',
    name: 'Framed photo, title and subtitle',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'title', 'calm'],
    slots: [
      slot({ id: 'a', panel: 'front', ...sub(P, 0.08, 0.06, 0.84, 0.6) }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.08, 0.7 + G, 0.84, 0.12) }),
      slot({ id: 's', panel: 'front', kind: 'text', role: 'caption', ...sub(P, 0.15, 0.86, 0.7, 0.06) }),
    ],
  }),
  layout({
    id: 'cv-1-front-window',
    name: 'Small window, title below',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'title', 'window'],
    slots: [
      slot({ id: 'a', panel: 'front', ...sub(P, 0.25, 0.16, 0.5, 0.5) }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.1, 0.72, 0.8, 0.12) }),
    ],
  }),
  layout({
    id: 'cv-0-front-title',
    name: 'Title and subtitle only',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'title', 'typographic'],
    slots: [
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.1, 0.38, 0.8, 0.16) }),
      slot({ id: 's', panel: 'front', kind: 'text', role: 'caption', ...sub(P, 0.15, 0.56, 0.7, 0.07) }),
    ],
  }),
  layout({
    id: 'cv-2-front-stack',
    name: 'Two photos stacked, title below',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'title'],
    slots: [
      slot({ id: 'a', panel: 'front', ...sub(P, 0.08, 0.06, 0.84, 0.36) }),
      slot({ id: 'b', panel: 'front', ...sub(P, 0.08, 0.42 + G, 0.84, 0.36) }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.08, 0.86, 0.84, 0.1) }),
    ],
  }),
  layout({
    id: 'cv-3-front-hero',
    name: 'Hero and two small, title below',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'title', 'hero'],
    slots: [
      slot({ id: 'a', panel: 'front', ...sub(P, 0.08, 0.06, 0.84, 0.46) }),
      slot({ id: 'b', panel: 'front', ...sub(P, 0.08, 0.52 + G, 0.84 / 2 - G / 2, 0.24) }),
      slot({ id: 'c', panel: 'front', ...sub(P, 0.08 + 0.84 / 2 + G / 2, 0.52 + G, 0.84 / 2 - G / 2, 0.24) }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.08, 0.86, 0.84, 0.1) }),
    ],
  }),
  layout({
    id: 'cv-4-front-grid',
    name: 'Four-up grid, title below',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'front', 'title', 'grid'],
    slots: [
      ...grid(sub(P, 0.08, 0.06, 0.84, 0.7), 'page', { cols: 2, rows: 2 }).map((s) => ({ ...s, panel: 'front' as const })),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.08, 0.82, 0.84, 0.12) }),
    ],
  }),

  // ---------- whole cover ----------
  layout({
    id: 'cv-1-wrap',
    name: 'One photo wraps the whole cover',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'wrap', 'full-bleed', 'title', 'hero'],
    marginFrac: 0,
    slots: [
      slot({ id: 'a', ...FULL_BOX }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.08, 0.76, 0.84, 0.14) }),
    ],
  }),
  layout({
    id: 'cv-2-front-back',
    name: 'Front photo and title, back photo and blurb',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'wrap', 'title'],
    slots: [
      slot({ id: 'a', panel: 'front', ...sub(P, 0.08, 0.06, 0.84, 0.62) }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.08, 0.72, 0.84, 0.12) }),
      slot({ id: 'b', panel: 'back', ...sub(P, 0.3, 0.12, 0.4, 0.4) }),
      slot({ id: 'x', panel: 'back', kind: 'text', role: 'body', ...sub(P, 0.15, 0.58, 0.7, 0.24) }),
    ],
  }),
  layout({
    id: 'cv-3-front-back-pair',
    name: 'Full-bleed front, two photos on the back',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'wrap', 'full-bleed', 'title'],
    marginFrac: 0,
    slots: [
      slot({ id: 'a', panel: 'front', ...FULL_BOX }),
      slot({ id: 't', panel: 'front', kind: 'text', role: 'title', ...sub(P, 0.06, 0.78, 0.88, 0.14) }),
      slot({ id: 'b', panel: 'back', ...sub(P, 0.1, 0.3, 0.38, 0.3) }),
      slot({ id: 'c', panel: 'back', ...sub(P, 0.52, 0.3, 0.38, 0.3) }),
      slot({ id: 'x', panel: 'back', kind: 'text', role: 'caption', ...sub(P, 0.1, 0.64, 0.8, 0.08) }),
    ],
  }),

  // ---------- back ----------
  layout({
    id: 'cv-1-back-small',
    name: 'Small photo and blurb on the back',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'back'],
    slots: [
      slot({ id: 'a', panel: 'back', ...sub(P, 0.3, 0.14, 0.4, 0.4) }),
      slot({ id: 'x', panel: 'back', kind: 'text', role: 'body', ...sub(P, 0.12, 0.6, 0.76, 0.24) }),
    ],
  }),
  layout({
    id: 'cv-0-back-blurb',
    name: 'Blurb only on the back',
    scope: 'cover',
    families: ALL_FAMILIES,
    tags: ['cover', 'back', 'typographic'],
    slots: [slot({ id: 'x', panel: 'back', kind: 'text', role: 'body', ...sub(P, 0.12, 0.2, 0.76, 0.5) })],
  }),
];
